import React, { useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import './ToolPage.css'
import './ShoppingList.css'
import './Tournament.css'
import { createTournament, setTournamentTeams, generateTournamentBracket } from './utils/tournamentApiClient'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const FORMATS = [
  { key: 'league', label: 'Pontos corridos', hint: 'Todo mundo joga contra todo mundo uma vez, classificação por pontos.' },
  { key: 'knockout', label: 'Mata-mata', hint: 'Chaveamento eliminatório direto: quem perde está fora.' },
  {
    key: 'groups',
    label: 'Grupos + mata-mata',
    hint: 'Times divididos em grupos jogam entre si, os melhores de cada grupo avançam pro mata-mata.',
  },
]

const TournamentCreate = () => {
  useDocumentMeta({
    title: 'Chaveamento e Campeonato ao Vivo | jarder.dev',
    description:
      'Cadastre os times, escolha pontos corridos, mata-mata ou grupos + mata-mata, e ' +
      'acompanhe a classificação ou o chaveamento avançar sozinho conforme você ' +
      'alimenta os placares. Grátis, sem cadastro.',
  })

  const history = useHistory()
  const [format, setFormat] = useState('league')
  const [teams, setTeams] = useState([])
  const [teamInput, setTeamInput] = useState('')
  const [groupCount, setGroupCount] = useState(2)
  const [advancesPerGroup, setAdvancesPerGroup] = useState(2)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('idle')

  const addTeam = () => {
    const trimmed = teamInput.trim()
    if (!trimmed) return
    if (teams.some((team) => team.toLowerCase() === trimmed.toLowerCase())) {
      setError('Esse time já foi adicionado.')
      return
    }
    setTeams((current) => [...current, trimmed])
    setTeamInput('')
    setError('')
  }

  const removeTeam = (team) => {
    setTeams((current) => current.filter((t) => t !== team))
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addTeam()
    }
  }

  const create = async () => {
    if (teams.length < 2) {
      setError('Cadastre pelo menos 2 times.')
      return
    }
    if (format === 'groups') {
      if (groupCount < 2) {
        setError('Defina pelo menos 2 grupos.')
        return
      }
      if (teams.length < groupCount * 2) {
        setError(`Pra ${groupCount} grupos, cadastre pelo menos ${groupCount * 2} times.`)
        return
      }
      const minGroupSize = Math.floor(teams.length / groupCount)
      if (advancesPerGroup < 1 || advancesPerGroup >= minGroupSize) {
        setError(
          `Com ${teams.length} times em ${groupCount} grupos, o menor grupo tem ${minGroupSize} times — ` +
            `escolha entre 1 e ${minGroupSize - 1} classificados por grupo.`,
        )
        return
      }
    }
    setStatus('loading')
    setError('')
    try {
      const { id, masterToken } = await createTournament(format)
      await setTournamentTeams(id, masterToken, teams)
      await generateTournamentBracket(
        id,
        masterToken,
        format === 'groups' ? { groupCount, advancesPerGroup } : undefined,
      )
      trackEvent('tool_generate', { tool: 'tournament', format, teams: teams.length })
      history.push(`/ferramentas/campeonato-ao-vivo/${id}/mestre#token=${masterToken}`)
    } catch (err) {
      setError(err.message || 'Não foi possível criar o torneio agora. Tente novamente.')
      setStatus('idle')
    }
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Chaveamento e Campeonato ao Vivo</h1>
      <p className="tool-disclaimer">
        Cadastre os times, escolha o formato e gere os confrontos. Depois
        é só ir alimentando os placares: a classificação ou o
        chaveamento avança sozinho.
      </p>

      <fieldset className="tournament-format-field">
        <legend>Formato</legend>
        <div className="tournament-format-options">
          {FORMATS.map((option) => (
            <label key={option.key} className="tournament-format-option">
              <input
                type="radio"
                name="format"
                checked={format === option.key}
                onChange={() => setFormat(option.key)}
              />
              <span>
                <strong>{option.label}</strong>
                <span className="tournament-format-hint">{option.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {format === 'groups' && (
        <div className="tournament-groups-config">
          <div className="tool-field">
            <label htmlFor="group-count">Número de grupos</label>
            <input
              id="group-count"
              type="number"
              min="2"
              value={groupCount}
              onChange={(e) => setGroupCount(Number(e.target.value))}
            />
          </div>
          <div className="tool-field">
            <label htmlFor="advances-per-group">Classificados por grupo</label>
            <input
              id="advances-per-group"
              type="number"
              min="1"
              value={advancesPerGroup}
              onChange={(e) => setAdvancesPerGroup(Number(e.target.value))}
            />
          </div>
        </div>
      )}

      <div className="tool-field">
        <label htmlFor="team-name">Times ({teams.length})</label>
        <div className="chip-input-row">
          <input
            id="team-name"
            type="text"
            value={teamInput}
            onChange={(e) => setTeamInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nome do time, aperte Enter"
          />
          <button type="button" className="tool-button tool-button-secondary" onClick={addTeam}>
            Adicionar
          </button>
        </div>
      </div>

      {teams.length > 0 && (
        <ul className="chip-list">
          {teams.map((team) => (
            <li className="chip" key={team}>
              {team}
              <button
                type="button"
                className="chip-remove"
                onClick={() => removeTeam(team)}
                aria-label={`Remover ${team}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="tool-error">{error}</p>}

      <button
        type="button"
        className="tool-button"
        onClick={create}
        disabled={status === 'loading'}
        style={{ marginTop: 20 }}
      >
        {status === 'loading' ? 'Gerando...' : 'Gerar confrontos'}
      </button>

      <div className="tool-info-section">
        <h2>Como funciona</h2>
        <p>
          Ao gerar, você vai pra tela de mestre, só você tem acesso a
          ela. Lá tem um link separado pra compartilhar: quem tiver o
          link acompanha a classificação ou o chaveamento ao vivo, sem
          poder alterar nada.
        </p>
        <p>
          No mata-mata (puro ou depois da fase de grupos), se o número de
          times não for uma potência de 2, alguns avançam de graça (bye)
          só na primeira rodada, como em qualquer chaveamento real — nenhum
          time pega bye duas vezes. Os times são sorteados aleatoriamente
          na hora de montar o chaveamento.
        </p>
        <p>
          Em grupos + mata-mata, os times são divididos aleatoriamente em
          grupos parecidos em tamanho, jogam todos contra todos dentro do
          próprio grupo, e os melhores colocados de cada grupo (por pontos,
          saldo de gols e gols marcados) avançam pro mata-mata assim que
          todos os jogos de grupo terminarem.
        </p>
      </div>
    </div>
  )
}

export default TournamentCreate
